/**
 * loginActions.js
 * Created by michaelbray on 2/13/18.
 */

import axios from 'axios';

import {
    LOGIN_SUCCESS,
    LOGIN_FAILED,
    LOCKOUT,
    LOGIN_BAD_MAGIC,
    LOGOUT_SUCCESS,
    HIDE_LOGIN_MODAL,
    MFA_REQUIRED,
    CSR_LOGIN_SUCCESS,
    CSR_CLEAR_SELECTED,
    SHOW_LOGIN_MODAL,
    SHOW_SIGN_IN_GUEST_MODAL,
    HIDE_SIGN_IN_GUEST_MODAL,
    ACCOUNT_SERVICE_STATUS_UPDATED,
    SHOW_CREATE_ACCOUNT_MODAL,
    HIDE_CREATE_ACCOUNT_MODAL,
    SHOW_RESET_MODAL,
    HIDE_RESET_MODAL
} from '../../actions/actionTypes';

import {
    fetchCart
} from '../../actions/navigation/cartActions';

import LocalStorage from '../../helpers/storage/LocalStorage';
import SessionStorage from '../../helpers/storage/SessionStorage';

import { getExternalCSRAuthHeader } from '../../helpers/securityHelper';


const loginSucceeded = (profile) => ({
    type: LOGIN_SUCCESS,
    profile
});

const loginFailed = () => ({
    type: LOGIN_FAILED
});

const lockout = () => ({
    type: LOCKOUT
});

const loginBadMagic = () => ({
    type: LOGIN_BAD_MAGIC
});

const logoutSucceeded = () => ({
    type: LOGOUT_SUCCESS
});

const mfaRequired = () => ({
    type: MFA_REQUIRED
});

const csrLoginSucceeded = (profile) => ({
    type: CSR_LOGIN_SUCCESS,
    profile
});

const csrClearCustomerSucceeded = () => ({
    type: CSR_CLEAR_SELECTED
})

const serviceStatusUpdated = (enabled, announcement, loginNotice) => ({
    type: ACCOUNT_SERVICE_STATUS_UPDATED,
    enabled,
    announcement,
    loginNotice
});

export const login = (loginData) => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/login`;
            const response = await axios.post(url, loginData);
            const profile = response.data;
            LocalStorage.setItem('recaccount', JSON.stringify(profile));
            // Set the user ID using signed-in user_id.
            if (window.dataLayer) {
                window.dataLayer.push({
                    userId: profile.account.account_id
                });
            }

            const { redirectUrl } = loginData;

            dispatch(loginSucceeded(profile));
            if (redirectUrl) {
                window.location.replace(redirectUrl);
            }
        }
        catch (error) {
            if (error.response && error.response.data && error.response.data.error === 'mfa') {
                dispatch(mfaRequired());
            }
            else if (error.response && error.response.data && error.response.data.error === 'lockout') {
                dispatch(lockout());
            }
            else {
                dispatch(loginFailed());
            }
        }
    };
};


export const magicLogin = (magicID) => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/login/magic/${magicID}`;
            const response = await axios.get(url);
            const profile = response.data;
            localStorage.setItem('recaccount', JSON.stringify(profile));
            // Set the user ID using signed-in user_id.

            if (window.dataLayer) {
                window.dataLayer.push({
                    userId: profile.account.account_id
                });
            }

            dispatch(loginSucceeded(profile));
        }
        catch (error) {
            if (error.response && error.response.data && error.response.data.error === 'badmagic') {
                dispatch(loginBadMagic());
            }
            else {
                dispatch(loginFailed());
            }
        }
    }
}

export const guestLogin = () => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/guest/login`;
            const response = await axios.post(url, {});
            const profile = response.data;
            LocalStorage.setItem('recaccount', JSON.stringify(profile));
            // Set the user ID using signed-in user_id.
            if (window.dataLayer) {
                window.dataLayer.push({
                    userId: profile.account.account_id
                });
            }

            dispatch(loginSucceeded(profile));
        }
        catch (error) {
            dispatch(loginFailed());
        }
    };
};

export const csrClearSelectedCustomer = () => {
    return (dispatch) => {
        localStorage.removeItem('recaccount');

        if (window.dataLayer) {
            window.dataLayer.push({
                userId: null,
            });
        }

        dispatch(csrClearCustomerSucceeded());
        dispatch(fetchCart()); // When we've cleared accounts, we need to fetch a cart to clear out what was there
    }
}

export const csrSession = (csrToken) => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/internal/csr_session`;
            const response = await axios.post(url, { token: csrToken }, { headers: { Authorization: `Bearer ${csrToken}`}});
            const profile = response.data;
            localStorage.setItem('csraccount', JSON.stringify(profile));

            dispatch(csrLoginSucceeded(profile));
            dispatch(csrClearSelectedCustomer());
        }
        catch (error) {
            dispatch(loginFailed());
        }
    }
}

export const csrLoggedIn = (profileData) => {
    return (dispatch) => {
        dispatch(csrLoginSucceeded(profileData));
    };
}

export const csrSelectCustomer = (accountID) => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/internal/csr_session/${accountID}`;
            const response = await axios.get(url, { headers: { Authorization: getExternalCSRAuthHeader() }});
            const profile = response.data;
            localStorage.setItem('recaccount', JSON.stringify(profile));
            // Set the user ID using signed-in user_id.

            if (window.dataLayer) {
                window.dataLayer.push({
                    userId: profile.account.account_id
                });
            }

            dispatch(loginSucceeded(profile));
            dispatch(fetchCart()); // When we've switched rec accounts, we need to fetch the cart again
        }
        catch (error) {
           dispatch(loginFailed());
        }
    }
}

export const loggedIn = (profileData) => {
    return (dispatch) => {
        dispatch(loginSucceeded(profileData));
    };
};

export const logout = () => {
    return (dispatch) => {
        LocalStorage.clear();
        SessionStorage.clear();
        dispatch(logoutSucceeded());
        window.location.assign("/");
    };
};

export const clearShowLoginModal = () => {
    return (dispatch) => {
        dispatch({
            type: HIDE_LOGIN_MODAL
        });
    };
};

export const showLoginModal = () => {
    return (dispatch) => {
        dispatch({
            type: SHOW_LOGIN_MODAL
        });
    };
};

export const showSignInGuestModal = () => {
    return (dispatch) => {
        dispatch({
            type: SHOW_SIGN_IN_GUEST_MODAL
        });
    };
};

export const clearShowSignInGuestModal = () => {
    return (dispatch) => {
        dispatch({
            type: HIDE_SIGN_IN_GUEST_MODAL
        });
    };
};

export const showCreateAccountModal = () => {
    return (dispatch) => {
        dispatch({
            type: SHOW_CREATE_ACCOUNT_MODAL
        });
    };
};

export const clearShowCreateAccountModal = () => {
    return (dispatch) => {
        dispatch({
            type: HIDE_CREATE_ACCOUNT_MODAL
        });
    };
};

export const showResetModal = () => {
    return (dispatch) => {
        dispatch({
            type: SHOW_RESET_MODAL
        });
    };
};

export const clearShowResetModal = () => {
    return (dispatch) => {
        dispatch({
            type: HIDE_RESET_MODAL
        });
    };
};

export const updateAccountServiceStatus = () => {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/accounts/status`;
            const response = await axios.get(url);

            const enabled = response.data.enabled;

            // COMMENT OUT THE LINES BELOW TO TEST THE ANNOUNCEMENT
            const announcement = response.data.announcement;

            // UNCOMMENT THE LINE BELOW TO TEST ANNOUNCEMENT
            //const announcement = {
            //    banner_title: "The White House Easter Egg Roll Lottery is Open!",
            //    banner_body: "%3Cp%3EEnter%20for%20your%20chance%20to%20visit%20the%20South%20Lawn%20of%20the%20White%20House%20and%20enjoy%20games%2C%20live%20entertainment%2C%20and%2C%20of%20course%2C%20the%20traditional%20egg%20roll.%20Click%20below%20to%20learn%20more%20and%20apply%20for%20tickets.%3C%2Fp%3E",
            //    banner_body_color: "dark",
            //    banner_background_color: "light",
            //    banner_enable_hiding: true,
            //    banner_enabled: true,
            //    banner_homepage_only: true,
            //    banner_image: "/img/announcements/2020WHEER-Open.jpg",
            //    banner_key: "maintenance_dev_key",
            //    banner_link: "/ticket/facility/250029",
            //    banner_link_button_title: "Enter Now",
            //    banner_photo_credit: "Official White House Photo by Tia Dufour"
            //}

            // COMMENT OUT THE LINE BELOW TO TEST THE LOG IN NOTICE
            const loginNotice = response.data.login_notice;

            // UNCOMMENT THE LINES BELOW TO TEST THE LOG IN NOTICE
            //const loginNotice = {
            //    notice_body: "%3Cp%20class%3D%22something%22%3EHello%2C%20World!%3C%2Fp%3E",
            //    notice_enabled: true,
            //    notice_title: ""
            //};

            dispatch(serviceStatusUpdated(enabled, announcement, loginNotice));
        }
        catch (error) {
            dispatch(serviceStatusUpdated(true, { banner_enabled: false }, { notice_enabled: false }));
        }
    };
};
